import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews } from '@/mocks/reviews';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pidtdi2vhsdh2d3590', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = (parsed as Record<string, unknown>)?.meta
        ? ((parsed as Record<string, unknown>)?.meta as Record<string, unknown>)?.message
        : (parsed as Record<string, unknown>)?.message;

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormErrorMessage('');
        form.reset();
      } else {
        const errorText = typeof serverMsg === 'string' ? serverMsg : (responseText || '提交失败，请稍后重试');
        if (typeof errorText === 'string' && errorText.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormErrorMessage(typeof errorText === 'string' ? errorText : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormErrorMessage('网络错误，请检查网络连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '8x8x水蜜桃',
    url: 'https://8x8xpeach.com',
    description: '热门内容平台，提供丰富视频资源和内容服务',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@8x8xpeach.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="了解8x8x水蜜桃的品牌故事、平台理念、用户口碑及联系方式。热门内容平台，致力于为用户提供优质的视频资源和社区体验。"
        keywords="8x8x水蜜桃,关于我们,品牌介绍,联系方式,用户口碑"
        canonicalUrl="https://8x8xpeach.com/about"
        schema={[orgSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-20">
          <div className="py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  关于 <strong>8x8x水蜜桃</strong>
                </h1>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  <strong>8x8x水蜜桃</strong>成立于2020年，是一个集视频资源与互动社区于一体的综合性数字内容平台。我们致力于为广大用户提供丰富、优质、安全的内容服务，打造极致的在线体验。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  平台汇聚了海量内容资源，覆盖多个分类频道，无论你喜欢什么类型的内容，都能在这里找到心仪之选。我们不仅提供高清流畅的播放体验，还构建了活跃友善的社区环境，让每一位用户都能参与讨论、分享观点。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  作为备受用户信赖的平台，我们始终坚持"用户第一、内容为王、安全为本"的核心理念。技术团队不断优化产品体验，运营团队精心筛选优质内容，客服团队7x24小时贴心服务——只为给你最好的体验。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-80">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20peach%20coral%20abstract%20illustration%20representing%20digital%20content%20platform%2C%20soft%20flowing%20shapes%2C%20creative%20modern%20minimalist%20design%2C%20dreamy%20ethereal%20atmosphere%2C%20elegant%20composition%20with%20warm%20lighting&width=600&height=500&seq=about-brand-peach&orientation=portrait"
                    alt="8x8x水蜜桃品牌形象"
                    className="w-full h-auto object-cover"
                    title="8x8x水蜜桃品牌形象"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '100万+', label: '注册用户' },
              { value: '50万+', label: '内容资源' },
              { value: '99.9%', label: '服务可用率' },
              { value: '7x24h', label: '客服在线' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-2xl bg-background-100"
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </section>

          {/* Reviews */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              用户口碑
            </h2>
            <p className="text-foreground-500 text-center mb-10">
              听听真实用户怎么说
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-6 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating
                            ? 'ri-star-fill text-accent-500'
                            : 'ri-star-line text-background-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">
                        {review.name}
                      </div>
                      <div className="text-xs text-foreground-400">
                        {review.location} · {review.date}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-foreground-500 text-center mb-10">
              有任何问题或建议？请随时与我们联系
            </p>

            <div className="max-w-xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 rounded-2xl bg-background-100">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">邮箱</div>
                  <div className="text-sm text-foreground-900 font-medium">support@8x8xpeach.com</div>
                </div>
                <div className="text-center p-5 rounded-2xl bg-background-100">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">客服</div>
                  <div className="text-sm text-foreground-900 font-medium">7x24小时在线</div>
                </div>
                <div className="text-center p-5 rounded-2xl bg-background-100">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">安全</div>
                  <div className="text-sm text-foreground-900 font-medium">SSL加密保护</div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 rounded-2xl p-6 md:p-8"
              >
                {formStatus === 'success' && (
                  <div className="mb-6 p-4 rounded-xl bg-green-50 text-green-700 text-sm flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    感谢您的留言！我们会尽快与您取得联系。
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-700 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formErrorMessage}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  提交留言
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}